import { inject, shallowReactive } from "vue";

export const confirmKey = Symbol("confirm");

const defaultOptions = Object.freeze({
  buttonTrueText: "Confirm",
  buttonFalseText: "Cancel",
  buttonTrueColor: "primary",
  buttonFalseColor: "grey",
  buttonTrueFlat: true,
  buttonFalseFlat: true,
  color: undefined,
  icon: "fa fa-warning",
  persistent: false,
  title: undefined,
  width: 450,
});

export function createConfirmService(installDefaults = {}) {
  const state = shallowReactive({
    current: null,
    open: false,
  });
  const queue = [];

  function activateNext() {
    if (state.current || queue.length === 0) {
      return;
    }
    state.current = queue.shift();
    state.open = true;
  }

  function confirm(message, options = {}) {
    return new Promise((resolve) => {
      queue.push({
        message,
        options: {
          ...defaultOptions,
          ...installDefaults,
          ...options,
        },
        resolve,
        settled: false,
      });
      activateNext();
    });
  }

  function settle(result) {
    if (!state.current || state.current.settled) {
      return;
    }
    state.current.settled = true;
    state.open = false;
    state.current.resolve(result);
  }

  function afterLeave() {
    if (state.open) {
      return;
    }
    state.current = null;
    activateNext();
  }

  return {
    state,
    confirm,
    settle,
    afterLeave,
  };
}

export function useConfirmService() {
  const service = inject(confirmKey);
  if (!service) {
    throw new Error("Confirm plugin is not installed");
  }
  return service;
}

export function useConfirm() {
  return useConfirmService().confirm;
}

export default {
  install(app, options = {}) {
    const { property = "$confirm", ...installDefaults } = options;
    const service = createConfirmService(installDefaults);

    app.provide(confirmKey, service);
    app.config.globalProperties[property] = service.confirm;
  },
};
